/**
 * SC-209: the one thing the rest of the app talks to about picture-in-picture.
 *
 * It decides which renderer this browser gets, keeps `isOpen` honest however the window goes away, records
 * the analytics, and closes the window when the user leaves the room. `open` and `close` are stable across
 * renders and read everything else from refs, which is what lets the video controller put them on its toolbar
 * surface long before the model inputs it needs exist.
 *
 * **`open()` returns nothing and swallows a rejection after one debug log.** That is the correct behaviour,
 * not laziness: Firefox's screen-share picker can outlive its transient activation (probe P5, 2026-09-20), so
 * an automatic open can be refused for reasons that are nobody's fault and mean nothing to the user. The
 * button is still there to click.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMaybeRoomContext } from '@livekit/components-react'

import {
  livekitAnalyticsBaseProps,
  stripUndefinedAnalyticsProps,
  trackEvent,
} from '../../../services/analyticsService'
import store from '../../../stores'
import { pipCapability } from './pipCapability'
import type { PipContent, PipMode, PipModel } from './pipModel'
import { pipAutoOpenBlocked } from './pipPreferences'
import { DocumentPipRenderer } from './renderers/DocumentPipRenderer'
import { VideoPipRenderer } from './renderers/VideoPipRenderer'
import { hasDocumentPip, hasVideoPip } from './renderers/documentPipApi'
import type { PipClosedBy, PipOpenReason, PipRenderer, PipSurface } from './renderers/PipRenderer'

/** `room-left` is ours: the room unmounted under an open window, which is neither the user nor the browser. */
export type PipCloseReason = PipClosedBy | 'room-left'

export interface PictureInPictureApi {
  mode: PipMode
  isOpen: boolean
  /**
   * Whether a click on the button could open anything. Always true for a document window; false on Safari
   * until a camera or a share is attached to its element, so the button can say why instead of failing.
   */
  canOpen: boolean
  /** Why the open window is open. `'user'` while closed, so nothing has to null-check it. */
  openReason: PipOpenReason
  open(reason?: PipOpenReason): void
  close(): void
  /** Called by `PipHost` on every model change; reaches the renderer open or not (§2.3). */
  publishModel(model: PipModel): void
  /** Where React should portal. `null` unless a document-backed window is open. */
  surface: PipSurface | null
}

export interface UsePictureInPictureInput {
  /** The application-wide switch — `isPipFeatureEnabled(workspaceSettings)`. */
  enabled: boolean
  /** Injected by the tests; production uses the real Document PiP renderer. */
  createRenderer?: () => PipRenderer
}

/** Once per session: a second one tells us nothing and the room is joined more than once a day. */
let unsupportedReported = false

/**
 * The Colyseus id comes from Redux; the LiveKit room name has to come from LiveKit. Redux's `roomName` is the
 * office's display name ("Design Office"), and every other LiveKit event carries the real one
 * (`<env>_<colyseusRoomId>`), so sending the display name would split PiP events off from the rest of the call.
 */
function analyticsBase(livekitRoomName: string | undefined): Record<string, unknown> {
  const room = store.getState()?.room
  return livekitAnalyticsBaseProps(room?.roomId, livekitRoomName)
}

export function usePictureInPicture({
  enabled,
  createRenderer,
}: UsePictureInPictureInput): PictureInPictureApi {
  const mode = useMemo<PipMode>(
    () =>
      pipCapability({
        flagOn: enabled,
        hasDocumentPip: hasDocumentPip(),
        hasVideoPip: hasVideoPip(),
      }),
    [enabled]
  )

  const [isOpen, setIsOpen] = useState(false)
  const [openReason, setOpenReason] = useState<PipOpenReason>('user')
  const [surface, setSurface] = useState<PipSurface | null>(null)
  const [canOpen, setCanOpen] = useState(true)

  const modeRef = useRef(mode)
  modeRef.current = mode

  // Read at the moment an event is sent, from a ref, so `open`/`close` stay stable across renders.
  const livekitRoom = useMaybeRoomContext()
  const livekitRoomNameRef = useRef<string | undefined>(undefined)
  livekitRoomNameRef.current = livekitRoom?.name || undefined

  const modelRef = useRef<PipModel | null>(null)
  const rendererRef = useRef<{ renderer: PipRenderer; dispose: () => void } | null>(null)
  const openedAtRef = useRef<number | null>(null)
  /**
   * The mode the open window was opened in. `modeRef` cannot stand in for it on the close: when the workspace
   * switch goes off, the render moves `modeRef` to `none` before the effect cleanup closes the window.
   */
  const openedModeRef = useRef<PipMode | null>(null)
  /** An open already in flight. `renderer.isOpen` is still false while the window is being created, so it
   *  cannot stand in for this: a second click would otherwise ride the same promise and report twice. */
  const openingRef = useRef(false)
  /**
   * The open we asked for and have not yet seen arrive. WebKit queues `enterpictureinpicture` as a task, so
   * the window can appear *after* `open()` has resolved; the renderer then says so through `subscribe`, and
   * this is what lets that late announcement be recorded as the open it is. Cleared on every way an open can
   * end without a window - a close, a cancel, a refusal, a mode change - so none can report a phantom open.
   */
  const pendingOpenRef = useRef<{ reason: PipOpenReason; model: PipModel } | null>(null)
  /** Set just before the deliberate close on unmount, so the close is attributed to leaving the room. */
  const leavingRef = useRef(false)
  /** What the cell last held, so a switch can name what it switched away from. */
  const lastContentRef = useRef<PipContent>(null)
  const createRendererRef = useRef(createRenderer)
  createRendererRef.current = createRenderer

  /** Set for the length of `close()`, so a close settled inside the click can be told from a later one. */
  const closingRef = useRef(false)
  /** The renderer found, inside the click, that the window the button read as open was already gone. */
  const staleCloseRef = useRef(false)

  const handleClosed = useCallback((by: PipClosedBy) => {
    if (closingRef.current && by === 'browser') staleCloseRef.current = true
    const startedAt = openedAtRef.current
    const openedMode = openedModeRef.current
    openedAtRef.current = null
    openedModeRef.current = null
    pendingOpenRef.current = null
    setIsOpen(false)
    setSurface(null)
    setOpenReason('user')

    if (startedAt === null) return
    const reason: PipCloseReason = leavingRef.current ? 'room-left' : by
    trackEvent(
      'APP_pip_closed',
      stripUndefinedAnalyticsProps({
        mode: openedMode ?? modeRef.current,
        durationMs: Date.now() - startedAt,
        closedBy: reason,
        ...analyticsBase(livekitRoomNameRef.current),
      }),
      'Livekit'
    )
  }, [])

  /**
   * The one place a window becomes open, whichever of the two signals gets here first: `open()` resolving
   * with the renderer open (Blink, and the document window), or the renderer announcing it later (WebKit).
   * Idempotent, so the second of the two is a no-op.
   */
  const markOpened = useCallback(
    (renderer: PipRenderer, reason: PipOpenReason, model: PipModel) => {
      if (openedAtRef.current !== null) return
      pendingOpenRef.current = null
      openedAtRef.current = Date.now()
      openedModeRef.current = modeRef.current
      setIsOpen(true)
      setOpenReason(reason)
      const opened = renderer.getSurface()
      trackEvent(
        'APP_pip_opened',
        stripUndefinedAnalyticsProps({
          mode: modeRef.current,
          reason,
          hasContent: model.content !== null,
          contentKind: model.content?.kind,
          tileCount: model.tiles.length,
          windowWidth: opened?.width,
          windowHeight: opened?.height,
          ...analyticsBase(livekitRoomNameRef.current),
        }),
        'Livekit'
      )
    },
    []
  )

  const ensureRenderer = useCallback((): PipRenderer | null => {
    if (rendererRef.current) return rendererRef.current.renderer
    if (modeRef.current === 'none') return null

    const renderer: PipRenderer =
      createRendererRef.current?.() ??
      (modeRef.current === 'video' ? new VideoPipRenderer() : new DocumentPipRenderer())
    const stopWatchingSurface = renderer.subscribe(() => {
      setSurface(renderer.getSurface())
      setCanOpen(renderer.canOpen ?? true)
      // Only an open we asked for: a renderer that reports itself open with nothing pending is not something
      // we can attribute to a reason, and the cancelled-open paths have cleared this on purpose.
      const pending = pendingOpenRef.current
      if (renderer.isOpen && pending && openedAtRef.current === null) {
        markOpened(renderer, pending.reason, pending.model)
      }
    })
    const stopWatchingClose = renderer.onClosed(handleClosed)

    rendererRef.current = {
      renderer,
      dispose: () => {
        // Destroy first, while we are still listening. Safari's renderer reports the close from inside
        // `destroy()` because the browser's `leavepictureinpicture` can no longer arrive; unsubscribing first
        // would drop that report and leave the open with no `APP_pip_closed`. `handleClosed` is idempotent.
        renderer.destroy?.()
        stopWatchingSurface()
        stopWatchingClose()
      },
    }
    setCanOpen(renderer.canOpen ?? true)
    return renderer
  }, [handleClosed, markOpened])

  const open = useCallback((reason: PipOpenReason = 'user') => {
    const model = modelRef.current
    if (!model) return

    // `mode === 'none'` has already been reported by the effect below, and the button that calls this is
    // not rendered in that case; nothing useful is left to do.
    if (modeRef.current === 'none') return

    // The "opens automatically" toggle governs every open nobody clicked for, so it is enforced here once
    // rather than trusted to each caller. Read now, not captured: it can flip at any time from the toolbar.
    if (reason !== 'user' && pipAutoOpenBlocked()) return

    const renderer = ensureRenderer()
    if (!renderer || renderer.isOpen || openingRef.current) return

    openingRef.current = true
    pendingOpenRef.current = { reason, model }
    void renderer
      .open(model, { reason })
      .then(() => {
        // Not open yet is one of two things. The open was cancelled while the window was still being created
        // (leaving the room does it) and the renderer threw the window away and resolved anyway - reporting
        // that would leave an `APP_pip_opened` with no close and no duration. Or the browser has not
        // announced the window yet (WebKit queues the event), and the `subscribe` above records it when it
        // does. The pending open is kept for the second; whatever cancelled the first has cleared it.
        if (!renderer.isOpen) return
        markOpened(renderer, reason, model)
      })
      .finally(() => {
        openingRef.current = false
      })
      .catch((error: unknown) => {
        pendingOpenRef.current = null
        /**
         * The one failure a user actually experiences: they click **Pop out video** and nothing happens.
         *
         * `console.warn`, not `debug`, because the feedback e-mail's ring buffer only captures `warn` and
         * `error`, and because `STRIP_CONSOLE` (`vite.config.ts`) would delete `log`/`debug`/`info` from any
         * build that turns it on. Without this line, the first bug report about this feature would be
         * unanswerable.
         *
         * Still not thrown: a refused gesture is expected often enough not to be exceptional, and the
         * button is still there to click again.
         */
        // Read off the shape, not `instanceof Error`: a `DOMException` does not extend `Error`, and
        // `NotAllowedError` and `InvalidStateError` — the two that actually happen — are DOMExceptions.
        const raw = (error as { name?: unknown } | null | undefined)?.name
        const name = typeof raw === 'string' ? raw : undefined
        console.warn('[PiP] could not open the window', error)
        trackEvent(
          'APP_pip_open_failed',
          stripUndefinedAnalyticsProps({
            mode: modeRef.current,
            reason,
            errorName: name,
            ...analyticsBase(livekitRoomNameRef.current),
          }),
          'Livekit'
        )
      })
  }, [ensureRenderer, markOpened])

  const close = useCallback(() => {
    // A close while the window is still being created cancels it; nothing that turns up later is ours.
    pendingOpenRef.current = null
    const renderer = rendererRef.current?.renderer
    closingRef.current = true
    staleCloseRef.current = false
    try {
      renderer?.close()
    } finally {
      closingRef.current = false
    }
    // The button read "open" for a window Safari had already closed on its own (SC-476), and the renderer
    // settled that inside this click. Nobody who clicks wanted to be told a window is closed that they cannot
    // see, so the click opens one: it is still the gesture `requestPictureInPicture()` needs, which would not
    // survive an await. A refused exit that only comes back later (promise) has lost it, and the next click
    // opens instead.
    if (staleCloseRef.current) {
      staleCloseRef.current = false
      if (renderer && renderer.canOpen !== false) open('user')
    }
  }, [open])

  const publishModel = useCallback((model: PipModel) => {
    modelRef.current = model
    // Open or not — §2.3. Phase 4's renderer needs models while closed or Safari refuses to open at all.
    rendererRef.current?.renderer.update(model)

    const was = lastContentRef.current
    lastContentRef.current = model.content
    // Only while the window is open: a change with nothing on screen is not something anybody saw, and the
    // state the window opened *with* is already on `APP_pip_opened`.
    if (openedAtRef.current !== null) {
      reportContentSwitch(was, model.content, analyticsBase(livekitRoomNameRef.current))
    }
  }, [])

  /**
   * R2: open the window when the user switches away from the Sevv tab (Chrome and Edge only).
   *
   * Media Session's `enterpictureinpicture` action is the only way into the window without a click: the
   * browser grants the handler the activation `requestWindow()` needs, which is why nothing may be awaited
   * between the handler and that call. Firefox and Safari have no such action, so `setActionHandler` throws
   * for it there; that is expected, and says nothing worth logging or counting.
   *
   * It does not fire on registration alone. Chrome also wants an https page that is capturing the mic or
   * the camera, and a site permission, "automatic picture-in-picture", that defaults to Ask: the first time,
   * Chrome asks inside the window, and a user who dismisses that has to re-allow it in site settings. The
   * page cannot read that permission, which is why the toolbar button and the share-start open stay.
   *
   * Held for as long as this hook is mounted, in Document mode only. The hook mounts while the LiveKit room
   * is connected (`LiveKitRoomConnector`), which in Sevv is the whole time you are in the office, not only
   * while someone is nearby. That is deliberate (the user, 2026-10-13): the window opens even when you are
   * alone with nothing shared, so you can keep it up and talk to whoever walks over without going back to
   * the tab. Sevv always publishes the mic, muted or not, which is what satisfies Chrome's capture rule.
   * Chrome's own automatic picture-in-picture only ever fires for a page that holds this handler, so taking
   * it away on leaving the office is what guarantees nothing can pop a window open after that.
   *
   * The handler's `details` are ignored: the reason Chrome passes is named differently across versions, and
   * every way this fires (a tab switch, or another window covering the tab) is `'tab-switch'` to us.
   */
  useEffect(() => {
    if (mode !== 'document') return
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return

    const session = navigator.mediaSession
    const action = 'enterpictureinpicture' as MediaSessionAction

    try {
      // `open` checks the "opens automatically" toggle at the moment this fires.
      session.setActionHandler(action, () => open('tab-switch'))
    } catch {
      return
    }

    return () => {
      try {
        session.setActionHandler(action, null)
      } catch {
        // Nothing to undo, and nothing the user could act on.
      }
    }
  }, [mode, open])

  /**
   * R11: Safari's renderer has to exist from the moment the mode is known, not from the first click.
   *
   * It keeps a hidden `<video>` attached to a live track so that a later `requestPictureInPicture()` finds an
   * element with metadata already loaded — building it inside `open()` would hand Safari a `readyState` of 0
   * and the click would be refused. Chromium and Firefox have no such need: their renderer opens a window on
   * demand and owns nothing before that, so it stays lazy.
   */
  /**
   * Leaving the room takes the window with it, and the close has to be *attributed* to that.
   *
   * Declared before the effect that owns the renderer on purpose: React runs cleanups in declaration order,
   * so this one sets the flag first and the close below then reports `room-left` rather than `user`. The
   * closing itself belongs to that effect, which runs on a mode change as well as on unmount.
   */
  useEffect(() => {
    // Reset on every mount: StrictMode runs the cleanup once immediately after mounting, and a flag left set
    // would attribute every subsequent close to leaving the room.
    leavingRef.current = false
    return () => {
      leavingRef.current = true
    }
  }, [])

  useEffect(() => {
    // Only Safari needs the element to exist before the click; the others stay lazy.
    if (mode === 'video') {
      const renderer = ensureRenderer()
      // `PipHost` is a child, so its effects run first and its publish can land before this renderer exists
      // (a remount after the switch flips, say). Without the latest model the element stays empty — and the
      // button disabled — until something in the room happens to change.
      if (renderer && modelRef.current) renderer.update(modelRef.current)
    }

    // The switch is live Redux state, so the mode can change under a call — including to `none`, which takes
    // the toolbar button away with it. Whatever renderer exists has to be closed and disposed on *every* such
    // change, not only on the way out of `video`: a Document PiP window left open once `PipHost` has stopped
    // rendering into it is an always-on-top blank pane, still holding a screen wake lock and a `--sevv-*`
    // observer, with no way to close it from inside the app.
    return () => {
      pendingOpenRef.current = null
      rendererRef.current?.renderer.close()
      rendererRef.current?.dispose()
      rendererRef.current = null
    }
  }, [mode, ensureRenderer])

  /**
   * R13: report a browser that cannot do this at all, once, and only from inside a call.
   *
   * Only when the switch is ON and the browser still cannot — that is the number worth having, because it
   * says how much of the user base an engine is costing us. The switch being off is not reported at all: it
   * would fire once per page load for every user in every workspace that has not enabled the feature, which
   * is a per-session event carrying nothing the workspace setting does not already say.
   */
  useEffect(() => {
    if (mode !== 'none' || !enabled) return
    reportUnsupported('no-api', analyticsBase(livekitRoomNameRef.current))
  }, [mode, enabled])

  return { mode, isOpen, canOpen, openReason, open, close, publishModel, surface }
}

/** `null`, or an identity stable across re-renders — a new object for the same share is not a switch. */
function contentKey(content: PipContent): string | null {
  if (!content) return null
  return content.kind === 'share' ? `share:${content.participantId}` : `whiteboard:${content.whiteboardId}`
}

/**
 * R8: what the big cell changed to, and why.
 *
 * `cause` is derived rather than passed in because the model is the only thing that knows: nothing calls
 * this to announce an intention, it is observed after the fact. A source appearing where there was none is
 * `started`, the last one going away is `ended`, and one giving way to another is `replaced` — which under
 * D2's hold can only happen when the held source has ended, so it is worth being able to count separately.
 */
function reportContentSwitch(from: PipContent, to: PipContent, base: Record<string, unknown>): void {
  const fromKey = contentKey(from)
  const toKey = contentKey(to)
  if (fromKey === toKey) return

  trackEvent(
    'APP_pip_content_switched',
    stripUndefinedAnalyticsProps({
      from: from?.kind ?? 'none',
      to: to?.kind ?? 'none',
      cause: fromKey === null ? 'started' : toKey === null ? 'ended' : 'replaced',
      ...base,
    }),
    'Livekit'
  )
}

function reportUnsupported(reason: 'no-api', base: Record<string, unknown>): void {
  if (unsupportedReported) return
  unsupportedReported = true
  trackEvent('APP_pip_unsupported', stripUndefinedAnalyticsProps({ reason, ...base }), 'Livekit')
}
